import { useEffect, useMemo, useState } from "react";
import { isAdminRole, isHigherThanDistrictAdmin, UserRole } from "../../app/userSlice";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { clearSharedAlert, SharedAlert } from "../../app/siteSlice";
import AlertMessage from "../../components/AlertMessage";
import BroadcastAlerts from "../../components/BroadcastAlerts";
import { LoadingTextSkeleton } from "../../components/LoadingTextSkeleton";
import AdminHome from "./AdminHome";
import TeacherHome from "./TeacherHome";
import StudentHome from "./StudentHome";

export default function Home() {
	const dispatch = useAppDispatch();
	const {
		firstName,
		lastName,
		course,
		role,
		isTeachersWriterAccount,
		school,
		schools,
		district,
		districts,
	} = useAppSelector((state) => state.user);
	const sharedAlert = useAppSelector((state) => state.site.sharedAlert);

	const [alerts, setAlerts] = useState<Record<number, string>>();
	const [lastMessage, setLastMessage] = useState<SharedAlert>();

	const isAdmin = isAdminRole(role);

	useEffect(() => {
		if (sharedAlert) {
			setLastMessage(sharedAlert);
			dispatch(clearSharedAlert());
		}
	}, [sharedAlert]);

	const instructionText = useMemo(() => {
		const hasMultipleSchools = (schools?.length ?? 0) > 1;
		const hasMultipleDistricts = (districts?.length ?? 0) > 1;

		let instruction = "";

		switch (role) {
			case UserRole.SchoolAdmin:
				instruction = `view your school to update course information, manage users, create new or modify existing prompts, or view reports for your school${hasMultipleSchools ? "s" : ""}.`;
				break;
			case UserRole.DistrictAdmin:
				instruction = `view your schools, manage users, create new or modify existing prompts, or view reports for your district${hasMultipleDistricts ? "s" : ""}.`;
				break;
			case UserRole.Writer:
				instruction =
					"write practice essays, review essays written by your peers, take lessons to improve your understanding of writing, or view your reports.";
				break;
			case UserRole.Proctor:
				instruction =
					"create writing assignments, review completed peer reviews, assign lessons, view class reports, and manage your course.";
				break;
			default:
				instruction =
					"view all districts and schools, manage users, create new or modify existing prompts, or view reports.";
				break;
		}

		const pickerWord = !isAdmin
			? "course"
			: role === UserRole.SchoolAdmin && hasMultipleSchools
				? "school"
				: role === UserRole.DistrictAdmin && hasMultipleDistricts
					? "district"
					: "";

		const pickerText = pickerWord
			? ` To change your ${pickerWord}, click the ${pickerWord} name.`
			: "";

		return instruction + pickerText;
	}, [role, schools, districts, isAdmin]);

	const header = useMemo(() => {
		if (!isAdmin) {
			return course?.name;
		} else if (school && role === UserRole.SchoolAdmin) {
			return school.name;
		} else if (district && role === UserRole.DistrictAdmin) {
			return district.name;
		}
	}, [course, district, school, role]);

	return (
		<>
			<BroadcastAlerts className="w-full mb-8" alerts={alerts} />

			{lastMessage && (
				<AlertMessage
					className="mb-8"
					type={lastMessage.level}
					message={lastMessage.message}
					showIcon={lastMessage.showIcon}
					onClear={() => setLastMessage(undefined)}
				/>
			)}

			<section className="mb-8">
				<h1>
					{header || (role && isHigherThanDistrictAdmin(role)) ? (
						header
					) : (
						<LoadingTextSkeleton className="w-60 h-8" />
					)}
				</h1>
				<p className="mt-4">
					Welcome, {firstName} {lastName}
					{isTeachersWriterAccount ? " (Student)" : ""}! Use the tabs at the top of the
					screen to {instructionText}
				</p>
			</section>

			{role == UserRole.Writer && <StudentHome setAlerts={setAlerts} />}
			{role == UserRole.Proctor && <TeacherHome setAlerts={setAlerts} />}
			{isAdmin && <AdminHome setAlerts={setAlerts} />}
		</>
	);
}
